import * as React from 'react';
import { useMemo } from 'react';

import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveIcon from '@mui/icons-material/Remove';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';

import { EntityMetadataModel, ParameterMetadata } from '../../../../api/api';
import { CollectionExpressionModel } from '../../Models/CollectionExpressionModel';
import { ExpressionModel } from '../../Models/ExpressionModel';
import { ExpressionTypes } from '../../Models/ExpressionTypes';
import { IVariableScope } from '../../Models/VariableScopes/IVariableScope';
import ExpressionPlaceholder from './ExpressionPlaceholder';

export type ExpressionPlaceholderCollectionInlineProps = {
	expression: ExpressionModel | undefined;
	onExpressionChange?: (expression: ExpressionModel | undefined) => void;
	parameterMetadata: ParameterMetadata;
	variableScope: IVariableScope | undefined;
	contextMenuId?: string;
	expectedObjectType?: EntityMetadataModel;
	onSelectExpression?: React.MouseEventHandler<HTMLElement>;
};

export default function ExpressionPlaceholderCollectionInline({
	expression,
	onExpressionChange,
	parameterMetadata,
	variableScope,
	contextMenuId,
	expectedObjectType,
	onSelectExpression,
}: ExpressionPlaceholderCollectionInlineProps) {
	const collectionExpression = expression as CollectionExpressionModel | undefined;
	const items = collectionExpression?.Items ?? [];

	const itemParameterMetadata = useMemo(() => {
		return {
			name: 'item',
			displayName: 'Item',
			type: parameterMetadata.type,
			categories: [],
			expectedType: parameterMetadata.expectedType
				? {
						...parameterMetadata.expectedType,
						typeWrapInCollection: false,
						typeJsonSchema:
							parameterMetadata.expectedType.typeJsonSchema?.type === 'array'
								? parameterMetadata.expectedType.typeJsonSchema.items
								: parameterMetadata.expectedType.typeJsonSchema,
					}
				: undefined,
			order: 0,
			isCollection: false,
			isRequired: false,
		} as ParameterMetadata;
	}, [parameterMetadata]);

	const handleAddItem = () => {
		const newItems = [...items, undefined];
		const newExpression: CollectionExpressionModel = {
			Type: ExpressionTypes.Collection,
			Items: newItems,
		};
		onExpressionChange?.(newExpression);
	};

	const handleRemoveItem = (index: number) => {
		const newItems = items.filter((_, i) => i !== index);
		const newExpression: CollectionExpressionModel = {
			Type: ExpressionTypes.Collection,
			Items: newItems,
		};
		onExpressionChange?.(newExpression);
	};

	const handleItemChange = (index: number, itemExpression: ExpressionModel | undefined) => {
		const newItems = [...items];
		newItems[index] = itemExpression;
		const newExpression: CollectionExpressionModel = {
			Type: ExpressionTypes.Collection,
			Items: newItems,
		};
		onExpressionChange?.(newExpression);
	};

	return (
		<Box
			sx={{
				border: '1px solid',
				borderColor: 'divider',
				borderRadius: 1,
				padding: 2,
				backgroundColor: 'background.paper',
			}}
		>
			<Stack direction="column" spacing={2}>
				<Stack direction="row" alignItems="center" justifyContent="space-between">
					<Typography variant="subtitle2" fontWeight="bold">
						Collection Items
					</Typography>
					<Stack direction="row" alignItems="center" spacing={0.5}>
						{onSelectExpression && (
							<Button component="span" size="small" onClick={onSelectExpression}>
								Use expression
							</Button>
						)}
						<IconButton size="small" onClick={handleAddItem} title="Add item">
							<AddCircleIcon fontSize="small" />
						</IconButton>
					</Stack>
				</Stack>

				{items.length === 0 && (
					<Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
						No items. Click + to add an item.
					</Typography>
				)}

				{items.map((item, index) => (
					<Stack key={index} direction="row" spacing={1} alignItems="flex-start">
						<Box sx={{ flexGrow: 1 }}>
							<ExpressionPlaceholder
								expression={item as ExpressionModel | undefined}
								onExpressionChange={(newItem) => handleItemChange(index, newItem)}
								parameterMetadata={itemParameterMetadata}
								variableScope={variableScope}
								isEditable={true}
								contextMenuId={contextMenuId}
								expectedObjectType={expectedObjectType}
							/>
						</Box>
						<IconButton size="small" onClick={() => handleRemoveItem(index)} title="Remove item" sx={{ mt: 0.5 }}>
							<RemoveIcon fontSize="small" />
						</IconButton>
					</Stack>
				))}
			</Stack>
		</Box>
	);
}
